import React, { useEffect, useState } from "react";
import Pagination from "@mui/material/Pagination";
import { API_URLS } from "../../../../apiUrl";
import axiosInstance from "../../../../components/axiosInstance";
import { toast } from "react-toastify";

interface CollectorDetailsProps {
  data?: any;
  collectorId?: number;
  onClose?: () => void;
}

interface MappedItem {
  receipt_number: string;
  connection_number: string;
  consumer_name: string;
  holding_number: string | null;
  phone_number: string;
  ward_no: string;
  category_of_consumer: string;
  ferule_size: string;
  rate: string;
  arrear_due_till_march: string;
  total_monthly_charge_amount: string;
  total_month_number: string;
  receipt_date: string;
  total_amount_at_this_stage: string;
  available_amount_at_this_stage: string;
  monthly_data: {
    mapping_id: number;
    water_connection_bill_id: number;
    arrear_amount: number;
    is_arrear_bill: boolean;
    bill_month: string;
  }[];
  created_at: string;
  [key: string]: any;
}

interface CashierDeposit {
  name: string;
  amount: number;
  date: string;
  user_name: string;
  email: string;
}

interface ConsumerDetail {
  consumer_name: string;
  connection_number: string;
  phone_number: string | null;
  connection_start_date: string;
  total_mapped_by_collector: number;
  all_outstanding_paid: boolean;
  total_outstanding: number;
  receipts_paid_by_this_collector: string[];
}

interface ReportData {
  collector_name: string;
  username: string;
  total_mapped_amount: string;
  total_consumers_mapped: string;
  total_deposited_by_cashiers: string;
  deposited_cashiers_data: CashierDeposit[];
  consumer_details: ConsumerDetail[];
}

const LIMIT = 50;

const todayStr = new Date().toISOString().split("T")[0];

// ── Months Popup ───────────────────────────────────────────────────────────
const MonthsPopup = ({
  months,
  onClose,
}: {
  months: string[];
  onClose: () => void;
}) => (
  <div
    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 bg-blue-50 border-b border-blue-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
            <i className="ri-calendar-line text-blue-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">
            Billing Months
            <span className="ml-2 text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
              {months.length} month{months.length !== 1 ? "s" : ""}
            </span>
          </h3>
        </div>
        <button
          onClick={onClose}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-blue-100 transition text-gray-400 hover:text-gray-600"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      {/* Months Grid */}
      <div className="px-5 py-4">
        <div className="flex flex-wrap gap-2">
          {months.map((month, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 text-xs font-medium"
            >
              <i className="ri-calendar-check-line text-blue-400 text-xs"></i>
              {month}
            </span>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t bg-gray-50 flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-1.5 text-sm rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 transition font-medium"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

const DateFilters = ({
  startDate,
  endDate,
  onStartChange,
  onEndChange,
  onReset,
}: {
  startDate: string;
  endDate: string;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
  onReset: () => void;
}) => (
  <div className="flex items-end gap-3 mb-4 flex-wrap">
    <div className="flex flex-col">
      <label className="text-xs text-gray-500 mb-1 font-medium">Start Date</label>
      <input
        type="date"
        value={startDate}
        onChange={(e) => onStartChange(e.target.value)}
        className="border border-gray-300 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
    </div>
    <div className="flex flex-col">
      <label className="text-xs text-gray-500 mb-1 font-medium">End Date</label>
      <input
        type="date"
        value={endDate}
        onChange={(e) => onEndChange(e.target.value)}
        className="border border-gray-300 px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
    </div>
    <button
      onClick={onReset}
      className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition font-medium"
    >
      <i className="ri-refresh-line"></i> Reset
    </button>
  </div>
);

const CollectorDetails: React.FC<CollectorDetailsProps> = ({
  data = null,
  collectorId = 0,
  onClose = () => {},
}) => {
  const [activeTab, setActiveTab] = useState<"transactions" | "report">(
    "transactions"
  );

  // ── Months popup state ──
  const [monthsPopup, setMonthsPopup] = useState<string[] | null>(null);

  // ── Track if we can go back in browser history ──
  const pushedState = React.useRef(false);

  useEffect(() => {
    // Push a state so browser back button works
    window.history.pushState({ collectorDetails: true }, "");
    pushedState.current = true;

    const handlePopState = () => {
      onClose();
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [onClose]);

  // ── Transactions state ──
  const [mappedList, setMappedList] = useState<MappedItem[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txPage, setTxPage] = useState(1);
  const [txTotalPages, setTxTotalPages] = useState(1);
  const [txStartDate, setTxStartDate] = useState(todayStr);
  const [txEndDate, setTxEndDate] = useState(todayStr);

  // ── Report state ──
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [rptStartDate, setRptStartDate] = useState(todayStr);
  const [rptEndDate, setRptEndDate] = useState(todayStr);

  // cashier pagination (client-side)
  const [cashierPage, setCashierPage] = useState(1);
  const cashierList = reportData?.deposited_cashiers_data || [];
  const cashierTotalPages = Math.max(
    1,
    Math.ceil(cashierList.length / LIMIT)
  );
  const pagedCashiers = cashierList.slice(
    (cashierPage - 1) * LIMIT,
    cashierPage * LIMIT
  );

  // consumer details pagination (client-side)
  const [consumerPage, setConsumerPage] = useState(1);
  const consumerList = reportData?.consumer_details || [];
  const consumerTotalPages = Math.max(
    1,
    Math.ceil(consumerList.length / LIMIT)
  );
  const pagedConsumers = consumerList.slice(
    (consumerPage - 1) * LIMIT,
    consumerPage * LIMIT
  );

  // ── Fetch transactions ──
 useEffect(() => {
    if (!collectorId) return;
    const municipalty_id = localStorage.getItem("selected_collector_municipality") || localStorage.getItem("municipality_id_v2") || "";
    
    const fetchTransactions = async () => {
      setTxLoading(true);
      try {
        const response = await axiosInstance.post(
          API_URLS.collectorMappedList,
          {
            collector_id: collectorId,
            limit: LIMIT,
            page: txPage,
            start_date: txStartDate,
            end_date: txEndDate,
            municipality_id: municipalty_id
          }
        );
        setMappedList(response.data?.data?.data || []);
        const pagination = response.data?.data?.pagination;
        setTxTotalPages(pagination?.lastPage || 1);
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch transactions",
          { position: "top-right", autoClose: 3000 }
        );
      } finally {
        setTxLoading(false);
      }
    };
    fetchTransactions();
  }, [collectorId, txPage, txStartDate, txEndDate]);

  useEffect(() => {
    setTxPage(1);
  }, [txStartDate, txEndDate]);

  // ── Fetch report ──
  const fetchReport = async () => {
    if (!collectorId) return;
    setReportLoading(true);
    try {
      const res = await axiosInstance.post(API_URLS.collectorReport, {
        start_date: rptStartDate,
        end_date: rptEndDate,
        page: 1,
        limit: LIMIT,
        collector_id: collectorId,
      });
      setReportData(res.data?.data || null);
      setCashierPage(1);
      setConsumerPage(1);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch report",
        { position: "top-right", autoClose: 3000 }
      );
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "report") fetchReport();
  }, [activeTab, collectorId, rptStartDate, rptEndDate]);

  useEffect(() => {
    setCashierPage(1);
    setConsumerPage(1);
  }, [rptStartDate, rptEndDate]);

  return (
    // ✅ Full screen — no modal overlay, takes full viewport
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-100 overflow-hidden">

      {/* ── Top Navbar ── */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b shadow-sm flex-shrink-0">
        {/* Left side — back buttons */}
        <div className="flex items-center gap-2">
    

          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Back to List
          </button>
        </div>

        {/* Title */}
        <div className="flex flex-col items-center">
          <h2 className="text-base font-semibold text-gray-800 leading-tight">
            Collector Details
          </h2>
          {data && (
            <p className="text-xs text-gray-400 mt-0.5">
              {data.admin_name && (
                <span className="font-medium text-gray-600">
                  {data.admin_name}
                </span>
              )}
              {data.municipality_name && (
                <span> &mdash; {data.municipality_name}</span>
              )}
            </p>
          )}
        </div>

        {/* Close X */}
        <button
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition text-gray-500"
          title="Close"
        >
          {/* <i className="ri-close-line text-xl"></i> */}
        </button>
      </div>

      {/* ── Scrollable Content ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col gap-5">

          {/* ── Collector Info Cards ── */}
          {data && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: "Username", value: data.login_user_name },
                { label: "Email", value: data.email },
                {
                  label: "Role",
                  value:
                    data.role_name === "collector"
                      ? "Field Collector"
                      : "Counter Collector",
                },
                { label: "Ward", value: data.ward_no || "N/A" },
                { label: "Borough", value: data.borough_name || "N/A" },
                {
                  label: "Status",
                  value: data.status === 1 ? "Active" : "Inactive",
                  badge: true,
                  active: data.status === 1,
                },
              ].map((field) => (
                <div
                  key={field.label}
                  className="bg-white rounded-xl px-4 py-3 shadow-sm border"
                >
                  <p className="text-xs text-gray-400 mb-1">{field.label}</p>
                  {field.badge ? (
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        field.active
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {field.value}
                    </span>
                  ) : (
                    <p className="text-sm font-medium text-gray-700 truncate">
                      {field.value || "—"}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="flex border-b px-4">
              {(["transactions", "report"] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-3 px-5 text-sm font-medium border-b-2 capitalize transition ${
                    activeTab === tab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i
                    className={`${
                      tab === "transactions"
                        ? "ri-receipt-line"
                        : "ri-bar-chart-line"
                    } mr-1.5`}
                  ></i>
                  {tab}
                </button>
              ))}
            </div>

            <div className="p-5">

              {activeTab === "transactions" && (
                <>
                  <DateFilters
                    startDate={txStartDate}
                    endDate={txEndDate}
                    onStartChange={setTxStartDate}
                    onEndChange={setTxEndDate}
                    onReset={() => {
                      setTxStartDate(todayStr);
                      setTxEndDate(todayStr);
                    }}
                  />

                  {txLoading ? (
                    <div className="flex justify-center items-center py-16">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : mappedList.length === 0 ? (
                    <div className="text-center py-14 text-gray-400 text-sm">
                      No transactions found for this date range.
                    </div>
                  ) : (
                    <>
                      <div className="rounded-lg border overflow-hidden mb-4">
                        <div className="overflow-x-auto">
                          <table className="min-w-full table-auto text-xs">
                            <thead className="bg-gray-100 border-b">
                              <tr>
                                {[
                                  "Sl. No.",
                                  "Receipt No.",
                                  "Connection No.",
                                  "Consumer Name",
                                  "Phone Number",
                                  "Ward",
                                  "Category",
                                  "Rate (₹)",
                                  "Billing Month",
                                  "Total Amount (₹)",
                                  ...(data.role_name !== "collector"
                                    ? ["Mapped Balance (₹)"]
                                    : ["Available (₹)"]),
                                  "Receipt Date",
                                ].map((h) => (
                                  <th
                                    key={h}
                                    className="py-2.5 px-3 text-left text-gray-600 font-semibold whitespace-nowrap bg-gray-100"
                                  >
                                    {h}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {mappedList.map((item, index) => {
                                const billMonths: string[] =
                                  item.monthly_data?.map(
                                    (m: any) => m.bill_month
                                  ) || [];
                                const receiptDate = item.receipt_date
                                  ? new Date(
                                      item.receipt_date
                                    ).toLocaleDateString("en-IN", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })
                                  : "N/A";
                                return (
                                  <tr
                                    key={item.receipt_number ?? index}
                                    className="border-b hover:bg-gray-50 transition"
                                  >
                                    <td className="py-2 px-3 text-gray-400">
                                      {(txPage - 1) * LIMIT + index + 1}
                                    </td>
                                    <td className="py-2 px-3 font-mono">
                                      {item.receipt_number || "N/A"}
                                    </td>
                                    <td className="py-2 px-3">
                                      {item.connection_number || "N/A"}
                                    </td>
                                    <td className="py-2 px-3 whitespace-nowrap">
                                      {item.consumer_name || "N/A"}
                                    </td>
                                    <td className="py-2 px-3">
                                      {item.phone_number || "N/A"}
                                    </td>
                                    <td className="py-2 px-3">
                                      {item.ward_no || "N/A"}
                                    </td>
                                    <td className="py-2 px-3">
                                      {item.category_of_consumer || "N/A"}
                                    </td>
                                    <td className="py-2 px-3">
                                      ₹{item.rate || "0"}
                                    </td>
                                    <td className="py-2 px-3 whitespace-nowrap">
                                      {billMonths.length > 0 ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setMonthsPopup(billMonths)
                                          }
                                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-semibold transition cursor-pointer border border-blue-200"
                                        >
                                          <i className="ri-calendar-line text-xs"></i>
                                          {billMonths.length} Month
                                          {billMonths.length !== 1 ? "s" : ""}
                                        </button>
                                      ) : (
                                        <span className="text-gray-400">N/A</span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 font-medium">
                                      ₹{item.total_amount_at_this_stage || "0"}
                                    </td>
                                    <td className="py-2 px-3">
                                      <span
                                        className={`font-medium ${
                                          data.role_name === "collector"
                                            ? Number(
                                                item.available_amount_at_this_stage
                                              ) < 0
                                              ? "text-red-600"
                                              : "text-green-600"
                                            : "text-green-600"
                                        }`}
                                      >
                                        ₹
                                        {data.role_name === "collector"
                                          ? item.available_amount_at_this_stage
                                          : Math.abs(
                                              Number(
                                                item.available_amount_at_this_stage
                                              )
                                            )}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-gray-600 whitespace-nowrap">
                                      {receiptDate}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="flex justify-center">
                        <Pagination
                          count={txTotalPages}
                          page={txPage}
                          onChange={(_, v) => setTxPage(v)}
                          color="primary"
                          size="small"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ════ REPORT TAB ════ */}
              {activeTab === "report" && (
                <>
                  <DateFilters
                    startDate={rptStartDate}
                    endDate={rptEndDate}
                    onStartChange={setRptStartDate}
                    onEndChange={setRptEndDate}
                    onReset={() => {
                      setRptStartDate(todayStr);
                      setRptEndDate(todayStr);
                    }}
                  />

                  {reportLoading ? (
                    <div className="flex justify-center items-center py-16">
                      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : !reportData ? (
                    <div className="text-center py-14 text-gray-400 text-sm">
                      No report data found for this date range.
                    </div>
                  ) : (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        {[
                          {
                            label: "Total Consumers Mapped",
                            value: reportData.total_consumers_mapped,
                            icon: "ri-group-line",
                            color: "text-green-600 bg-green-50",
                          },
                          {
                            label: "Total Mapped Amount",
                            value: `₹${reportData.total_mapped_amount}`,
                            icon: "ri-money-rupee-circle-line",
                            color: "text-orange-600 bg-orange-50",
                          },
                          {
                            label: "Total Deposited by Cashiers",
                            value: `₹${reportData.total_deposited_by_cashiers}`,
                            icon: "ri-secure-payment-line",
                            color: "text-teal-600 bg-teal-50",
                          },
                        ].map((card) => (
                          <div
                            key={card.label}
                            className="bg-white border rounded-xl px-5 py-4 flex items-center gap-4 shadow-sm"
                          >
                            <div
                              className={`w-10 h-10 flex items-center justify-center rounded-lg text-xl ${card.color}`}
                            >
                              <i className={card.icon}></i>
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-gray-400">
                                {card.label}
                              </p>
                              <p className="text-base font-semibold text-gray-800 truncate">
                                {card.value}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* ── Cashier Deposits Table ── */}
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-bank-line mr-1.5 text-gray-400"></i>
                        Cashier Deposits
                      </h3>

                      {cashierList.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">
                          No cashier deposits in this date range.
                        </div>
                      ) : (
                        <>
                          <div className="rounded-lg border overflow-hidden mb-3">
                            <div className="overflow-x-auto">
                              <table className="min-w-full table-auto text-xs">
                                <thead className="bg-gray-100 border-b">
                                  <tr>
                                    {[
                                      "#",
                                      "Cashier Name",
                                      "Username",
                                      "Email",
                                      "Amount (₹)",
                                      "Date",
                                    ].map((h) => (
                                      <th
                                        key={h}
                                        className="py-2.5 px-3 text-left text-gray-600 font-semibold whitespace-nowrap bg-gray-100"
                                      >
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {pagedCashiers.map((cashier, index) => (
                                    <tr
                                      key={index}
                                      className="border-b hover:bg-gray-50 transition"
                                    >
                                      <td className="py-2 px-3 text-gray-400">
                                        {(cashierPage - 1) * LIMIT + index + 1}
                                      </td>
                                      <td className="py-2 px-3 font-medium whitespace-nowrap">
                                        {cashier.name}
                                      </td>
                                      <td className="py-2 px-3 text-gray-500">
                                        {cashier.user_name}
                                      </td>
                                      <td className="py-2 px-3 text-gray-500">
                                        {cashier.email}
                                      </td>
                                      <td className="py-2 px-3 font-semibold text-green-700">
                                        ₹{cashier.amount}
                                      </td>
                                      <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                                        {new Date(
                                          cashier.date
                                        ).toLocaleDateString("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="flex justify-center mb-2">
                            <Pagination
                              count={cashierTotalPages}
                              page={cashierPage}
                              onChange={(_, v) => setCashierPage(v)}
                              color="primary"
                              size="small"
                            />
                          </div>
                        </>
                      )}

                      <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
                        <i className="ri-user-line mr-1.5 text-gray-400"></i>
                        Consumer Details
                      </h3>

                      {consumerList.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">
                          No consumer details in this date range.
                        </div>
                      ) : (
                        <>
                          <div className="rounded-lg border overflow-hidden mb-3">
                            <div className="overflow-x-auto">
                              <table className="min-w-full table-auto text-xs">
                                <thead className="bg-gray-100 border-b">
                                  <tr>
                                    {[
                                      "#",
                                      "Consumer Name",
                                      "Connection No.",
                                      "Phone",
                                      "Connection Date",
                                      "Mapped Amount (₹)",
                                      "Outstanding (₹)",
                                    ].map((h) => (
                                      <th
                                        key={h}
                                        className="py-2.5 px-3 text-left text-gray-600 font-semibold whitespace-nowrap bg-gray-100"
                                      >
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {pagedConsumers.map((consumer, index) => (
                                    <tr
                                      key={index}
                                      className="border-b hover:bg-gray-50 transition"
                                    >
                                      <td className="py-2 px-3 text-gray-400">
                                        {(consumerPage - 1) * LIMIT +
                                          index +
                                          1}
                                      </td>
                                      <td className="py-2 px-3 font-medium whitespace-nowrap">
                                        {consumer.consumer_name || "N/A"}
                                      </td>
                                      <td className="py-2 px-3 font-mono">
                                        {consumer.connection_number || "N/A"}
                                      </td>
                                      <td className="py-2 px-3 text-gray-500">
                                        {consumer.phone_number || "N/A"}
                                      </td>
                                      <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                                        {consumer.connection_start_date
                                          ? new Date(
                                              consumer.connection_start_date
                                            ).toLocaleDateString("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                            })
                                          : "N/A"}
                                      </td>
                                      <td className="py-2 px-3 font-semibold text-blue-700">
                                        ₹{consumer.total_mapped_by_collector}
                                      </td>
                                      <td className="py-2 px-3">
                                        <span
                                          className={`font-medium ${
                                            consumer.total_outstanding > 0
                                              ? "text-red-600"
                                              : "text-green-600"
                                          }`}
                                        >
                                          ₹
                                          {Number(
                                            consumer.total_outstanding
                                          ).toFixed(2)}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <div className="flex justify-center">
                            <Pagination
                              count={consumerTotalPages}
                              page={consumerPage}
                              onChange={(_, v) => setConsumerPage(v)}
                              color="primary"
                              size="small"
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

        

        </div>
      </div>
      {monthsPopup && (
        <MonthsPopup
          months={monthsPopup}
          onClose={() => setMonthsPopup(null)}
        />
      )}
    </div>
  );
};

export default CollectorDetails;